import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { CheckCircle2, Clock } from "lucide-react";
import { inr } from "@/lib/api";

const DAY = 86400000;
const fmt = (ms) => new Date(ms).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });

export const buildSchedule = (inv) => {
  if (!inv.approved_at || !inv.duration_days) return [];
  const start = new Date(inv.approved_at).getTime();
  const step = inv.credit_schedule === "daily" ? 1 : inv.credit_schedule === "every_15_days" ? 15 : inv.duration_days;
  const count = Math.max(1, Math.floor(inv.duration_days / step));
  const credit = inv.expected_interest / count;
  const now = Date.now();
  let cumulative = 0;
  return Array.from({ length: count }, (_, i) => {
    const day = (i + 1) * step;
    const date = start + day * DAY;
    cumulative += credit;
    const last = i === count - 1;
    return { n: i + 1, day, date, credit, cumulative, principal: last ? inv.amount : 0, credited: inv.status === "completed" || date <= now };
  });
};

export const scheduleLabel = (s) => ({ daily: "Daily credits", every_15_days: "Credited every 15 days", maturity: "Paid at maturity" }[s] || "Paid at maturity");

export const PayoutLedgerDialog = ({ inv, children }) => {
  const rows = buildSchedule(inv);
  const done = rows.filter((r) => r.credited);
  const earned = done.reduce((s, r) => s + r.credit, 0);
  return (
    <Dialog>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="max-w-2xl w-[95vw] max-h-[90vh] overflow-y-auto bg-[#0A0A0A] border-[#D4AF37]/30 text-white p-8 rounded-none" data-testid={`ledger-dialog-${inv.id}`}>
        <DialogHeader className="text-left">
          <p className="font-accent text-[10px] tracking-[0.25em] uppercase text-[#D4AF37]">{scheduleLabel(inv.credit_schedule)} · {inv.return_percent}% total</p>
          <DialogTitle className="font-display text-2xl tracking-tight text-white">{inv.plan_title}</DialogTitle>
          <DialogDescription className="text-white/50 text-sm">
            {inr(inv.amount)} invested · {rows.length} credit{rows.length === 1 ? "" : "s"} of {inr(rows[0]?.credit || 0)} · principal returned on day {inv.duration_days}
          </DialogDescription>
        </DialogHeader>
        <div className="mt-5 grid grid-cols-3 gap-4 border border-white/10 p-4">
          <div><p className="font-accent text-[9px] tracking-[0.2em] uppercase text-white/35">Credited so far</p><p className="text-emerald-400 text-lg mt-1" data-testid="ledger-earned">{inr(earned)}</p></div>
          <div><p className="font-accent text-[9px] tracking-[0.2em] uppercase text-white/35">Credits done</p><p className="text-white text-lg mt-1" data-testid="ledger-done">{done.length} / {rows.length}</p></div>
          <div><p className="font-accent text-[9px] tracking-[0.2em] uppercase text-white/35">Remaining</p><p className="text-[#D4AF37] text-lg mt-1">{inr(inv.expected_interest - earned)}</p></div>
        </div>
        <div className="mt-5 border border-white/10" data-testid="ledger-table">
          <div className="grid grid-cols-12 gap-2 px-4 py-2 border-b border-white/10 font-accent text-[9px] tracking-[0.2em] uppercase text-white/35">
            <span className="col-span-1">#</span><span className="col-span-3">Date</span><span className="col-span-3">Credit</span><span className="col-span-3">Cumulative</span><span className="col-span-2 text-right">Status</span>
          </div>
          {rows.map((r) => (
            <div key={r.n} className={`grid grid-cols-12 gap-2 px-4 py-2 border-b border-white/5 last:border-0 text-xs items-center ${r.credited ? "" : "opacity-60"}`} data-testid={`ledger-row-${inv.id}-${r.n}`}>
              <span className="col-span-1 text-white/40">D{r.day}</span>
              <span className="col-span-3 text-white/70">{fmt(r.date)}</span>
              <span className="col-span-3 text-white">{inr(r.credit)}{r.principal ? <span className="text-[#D4AF37]"> + {inr(r.principal)}</span> : null}</span>
              <span className="col-span-3 text-white/60">{inr(r.cumulative)}</span>
              <span className={`col-span-2 flex items-center justify-end gap-1 font-accent text-[9px] tracking-[0.15em] uppercase ${r.credited ? "text-emerald-400" : "text-white/40"}`}>
                {r.credited ? <CheckCircle2 size={11} /> : <Clock size={11} />}{r.credited ? "Credited" : "Upcoming"}
              </span>
            </div>
          ))}
        </div>
        <p className="mt-4 text-[11px] text-white/35">Schedule is indicative and based on your approval date. Actual credits are released by the MetrixWorks desk to your registered bank account.</p>
      </DialogContent>
    </Dialog>
  );
};
